import config from 'constants/config'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { runChannelMarkupV2SessionRound } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupSource, ChannelMarkupV2SessionContext } from 'lib/channelMarkup/channelMarkupV2'

export const getChannelMarkup = (state: App.State) => config.ENABLE_CHANNEL_MARKUP ? state.marketing.channelMarkup : undefined

/**
 * The channel markup v2 session (user + market) context used by `getChannelMarkupSource`
 * to run the session round. `siteLocation` is omitted: map-time markup is
 * one-price-everywhere and cannot satisfy site-location conditions.
 */
export const getChannelMarkupV2SessionContext = (state: App.State): ChannelMarkupV2SessionContext => ({
  region: state.geo.currentRegionCode,
  currency: state.geo.currentCurrency,
  // Subscriber tier only carries meaning for a member who can currently view LuxPlus+
  // benefits; everyone else evaluates as `null`.
  subscriberTier: getCanViewLuxPlusBenefits(state) ? (state.auth.account.luxPlus.member.subscription.item?.tier ?? null) : null,
  roles: state.auth.account.roles,
})

/**
 * The mode-tagged markup source to thread through the offer mappers. Returns the v2
 * session state (evaluated once from the client-init config) when channel markup v2 is
 * active and its config is available; otherwise falls back to the v1 `channelMarkup`.
 */
export const getChannelMarkupSource = (state: App.State): ChannelMarkupSource => {
  const channelMarkupConfig = state.marketing.clientInitConfig?.channelMarkupInitConfig
  const isV2Active = config.ENABLE_CHANNEL_MARKUP
    && config.channelMarkupEvaluationMode === 'v2'
    && !!channelMarkupConfig
    && !!state.geo.currentRegionCode
    && !!state.geo.currentCurrency

  if (isV2Active) {
    const { state: sessionState } = runChannelMarkupV2SessionRound(channelMarkupConfig, getChannelMarkupV2SessionContext(state))
    return { mode: 'v2', sessionState, blockList: channelMarkupConfig.globalOfferIdBlockList ?? [] }
  }

  return { mode: 'v1', channelMarkup: getChannelMarkup(state) }
}
